import { Component } from '@angular/core';
import { AbstractWidgetDirective } from '../../abstract-widget.directive';
import { ActionsSubject, Store } from '@ngrx/store';
import {
  DialogType,
  REQUIRED_VALIDATOR_KEY,
  UiWidget,
  WidgetConfiguration,
  WidgetValidator,
  WProperties,
  WRiskAssessmentProperties
} from '@pt/pt-models';
import { TranslateService } from '@ngx-translate/core';
import { QueryService } from '@pt/api-client';
import { DialogService } from '../../../../services/dialog.service';
import { WidgetDialogService } from '../../../../documents/services/widget-dialog.service';

import { NgxPermissionsService } from 'ngx-permissions';
import { WidgetUtils } from '../../widget.utils';
import { TreatmentWarning } from 'it/primatoo/openapi/api/org/schemas/treatment/TreatmentWarning';
import {
  WidgetRiskAssessmentV0Args,
  WidgetRiskAssessmentV0Implemented
} from '../v0/models/widget-risk-assessment-v0-args.dto';
import { WraValue } from '../v0/models/wraValue';
import { WRAValueAnswer } from '../v0/models/wraValueAnswer';
import { WraV0Row } from './models/wra-v0-row';
import { WraV0MeasureCategory } from './models/wra-v0-measure-category';
import { WraV0Utils } from './wra-v0.utils';
import { WraV0GlobalMeasureCheck } from './models/wra-v0-global-measure-check';
import { WraV0GlobalMeasureCheckValue } from './models/wra-v0-global-measure-check-value';
import { WraV0Header } from './models/wra-v0-header';
import { NgrxWidgetActions } from '../../../store';
import { WraV0Threat } from './models/wra-v0-threat';

@Component({
  selector: 'pt-wra-v0',
  template: `
    <pt-widget-layout
      *ngIf="internalStatus.enabled"
      [widget]="widget"

      [comment]="comment"
      [unsavedComment]="unsavedComment"
      [internalStatus]="internalStatus"
      [enableResetAll]="true"
      (clickResetAll)="clickResetAll()"
      (clickSaveWidget)='saveWidget(false)'
      (clickResetWidget)='resetWidget(widget)'
      (clickEditWidget)='dispatchEditState()'
      (clickReloadWidget)='reloadWidget(widget)'
      (clickCreateOpt)='createOptWidget()'
      (clickCommentWidget)='clickCommentWidget()'>

      <pt-wra-v0-global-measure-selector
        [warnings]="internalStatus.warnings"
        [editable]="internalStatus.viewMode === 'EDIT'"
        [selectedGlobalMeasure]="widgetValue?.globalMeasureCheckDocId"
        [widgetProperties]="widgetProperties"
        [queryGlobalMeasureCheckDocs]="globalMeasureCheckDocs"
        [queryGlobalMeasureCheck]="globalMeasureCheckValue"
        [answers]='widgetValue?.answers || []'
        (applyChangeGlobalMeasureChecked)="applyChange($event)"
        (alignGlobalMeasAnswerChanged)="applyGlobalMeasAnswerChanged($event)"
        (alignGlobalMeasListChanged)="alignGlobalMeasListChanged($event)"
        (loadGlobalMeasureCheck)="loadGlobalMeasureCheck($event)">
      </pt-wra-v0-global-measure-selector>

      <pt-widget-nodata [noDataLabel]="noDataLabel" *ngIf="widgetValue?.answers?.length === 0"></pt-widget-nodata>

      <pt-wra-v0-editor
        [editable]="internalStatus.viewMode === 'EDIT'"
        [header]="header"
        [threats]="threats"
        [implementedValues]="implementedValues"
        [rows]="rows"
        (changedRow)='changeRow($event)'>
      </pt-wra-v0-editor>

    </pt-widget-layout>

  `
})
export class WraV0Component extends AbstractWidgetDirective<WraV0Row[]> {
  public rows: WraV0Row[] = [];
  widgetProperties: WProperties = { riskAssessmentViewMode: WRiskAssessmentProperties.FILL_FROM_QUERY };
  header: WraV0Header;
  implementedValues: WidgetRiskAssessmentV0Implemented[];
  // la funzione is valid viene utilizzata anche dalla classe astratta e dall'HTML.
  // il suo valore è il risultato di un form che creo ogni volta che mi cambia il valore settando di conseguenza questa variabile
  // formValid = false;
  widgetValue: WraValue;
  globalMeasureCheckDocs: WraV0GlobalMeasureCheck[] = [];
  globalMeasureCheckValue: WraV0GlobalMeasureCheckValue[] = [];
  treatmentSelectedMeasure: string[];
  measureCategories: WraV0MeasureCategory[];
  threats: WraV0Threat[] = [];

  constructor(public store: Store,
              public actionsSubject: ActionsSubject,
              public translateService: TranslateService,
              public dialogService: DialogService,
              public widgetDialogService: WidgetDialogService,
              public service: QueryService,
              public permissionService: NgxPermissionsService) {
    super(store, actionsSubject, translateService, widgetDialogService, permissionService);
  }

  public applyGlobalMeasAnswerChanged(ignoreChange?: boolean) {
    if (ignoreChange) {
      this.store.dispatch(NgrxWidgetActions.disableWarnings({ request: { disabledWarnings: [TreatmentWarning.GLOBAL_MEAS_ANSWER_CHANGED] } }));
    } else {
      this.applyChange(this.widgetValue.globalMeasureCheckDocId, [TreatmentWarning.GLOBAL_MEAS_ANSWER_CHANGED]);
    }
  }

  public alignGlobalMeasListChanged(ignoreChange?: boolean) {
    if (ignoreChange) {
      this.store.dispatch(NgrxWidgetActions.disableWarnings({ request: { disabledWarnings: [TreatmentWarning.GLOBAL_MEAS_LIST_CHANGED] } }));
    } else {
      this.applyChange(this.widgetValue.globalMeasureCheckDocId, [TreatmentWarning.GLOBAL_MEAS_LIST_CHANGED]);
    }
  }

  public applyChange(docId: number, resetWarning?: TreatmentWarning[]) {
    const value: WraValue = {
      globalMeasureCheckDocId: docId,
      answers: []
    };

    if (!docId) {
      this.globalMeasureCheckValue = [];
    }
    const answers: WRAValueAnswer[] = this.widgetValue.answers ? JSON.parse(JSON.stringify(this.widgetValue.answers)) : [];
    answers.forEach((answer: WRAValueAnswer) => {
      const queryGlobalMeasure = this.globalMeasureCheckValue?.find((g) => g.uuid === answer.uuid && g.globalMeasureCheckDocId === docId);
      if (queryGlobalMeasure) {
        value.answers.push({
          uuid: queryGlobalMeasure.uuid,
          note: queryGlobalMeasure.note,
          implementedValue: queryGlobalMeasure.implementedValue
        });
      } else {
        value.answers.push(answer);
      }
    });

    this.widgetValue = value;
    this.saveWidget(true, resetWarning);
  }

  public loadGlobalMeasureCheck(docId?: number) {
    if (docId) {
      const args = this.widget.args as WidgetRiskAssessmentV0Args;
      const query = JSON.parse(JSON.stringify(args.queryGlobalMeasureCheck).replace('"<editor.globalMeasureCheckDocId>"', docId.toString()));
      const mapping = this.widget.args.queryGlobalMeasureCheck.mapping;
      this.service.resolve(query).subscribe(
        (queryResponse: unknown[]) => {
          this.globalMeasureCheckValue = WidgetUtils.mappingQueryResponse(queryResponse, mapping) as WraV0GlobalMeasureCheckValue[];
          this.rows = WraV0Utils.toWraV0Rows(
            this.widgetProperties,
            this.widgetValue,
            this.measureCategories,
            this.globalMeasureCheckValue);
        }, () => {
          this.dispatchErrorState();
        }
      );
    } else {
      this.globalMeasureCheckValue = [];
    }
  }

  loadWidgetTreatmentApplication(widget: UiWidget) {
    const args = widget.args as WidgetRiskAssessmentV0Args;
    if (args.queryFindTreatmentSecMeasures) {
      const query = JSON.parse(JSON.stringify(args.queryFindTreatmentSecMeasures));
      this.service.resolve(query).subscribe(
        (queryResponse: string[]) => {
          this.treatmentSelectedMeasure = queryResponse;

          this.rows = WraV0Utils.toWraV0Rows(
            this.widgetProperties,
            this.widgetValue,
            this.measureCategories,
            this.globalMeasureCheckValue);

        }, () => {
          this.dispatchErrorState();
        }
      );
    }
  }

  private loadQueryGlobalMeasureCheckDocs(widget: UiWidget) {
    const args = widget.args as WidgetRiskAssessmentV0Args;
    if (args.queryGlobalMeasureCheckDocs) {
      this.service.resolve(args.queryGlobalMeasureCheckDocs).subscribe(
        (queryResponse: unknown[]) => {
          this.globalMeasureCheckDocs = WidgetUtils.mappingQueryResponse(queryResponse, widget.args.queryGlobalMeasureCheckDocs.mapping) as WraV0GlobalMeasureCheck[];
          this.rows = WraV0Utils.toWraV0Rows(
            this.widgetProperties,
            this.widgetValue,
            this.measureCategories,
            this.globalMeasureCheckValue);

        }, () => {
          this.dispatchErrorState();
        }
      );
    }
  }

  loadWidget(widget: UiWidget): void {
    this.dispatchInitState();
    const args = widget.args as WidgetRiskAssessmentV0Args;
    this.widgetProperties = widget.properties;

    if (this.widgetProperties?.riskAssessmentViewMode === WRiskAssessmentProperties.FILL_FROM_TRIGGER_AND_SELECT) {
      // con l'opzione GlobalMeasureCHeckImportEnabled è necessario utilizzare altre 3 query;
      this.loadQueryGlobalMeasureCheckDocs(widget);
      this.loadWidgetTreatmentApplication(widget);
    }

    if (args.queryThreat) {
      this.service.resolve(args.queryThreat).subscribe(
        (queryResponse: unknown[]) => {
          const mapping = args.queryThreat.mapping;
          this.threats = WidgetUtils.mappingQueryResponse(queryResponse, mapping) as WraV0Threat[];
        });
    }


    this.service.resolve(args.query).subscribe(
      (queryResponse: unknown[]) => {
        const mapping = args.query.mapping;
        this.measureCategories = WidgetUtils.mappingQueryResponse(queryResponse, mapping) as WraV0MeasureCategory[];
        this.header = args.header;
        this.implementedValues = args.data?.implementedValues;
        this.widgetValue = JSON.parse(JSON.stringify(widget.value));

        if (this.widgetValue?.globalMeasureCheckDocId) {
          this.loadGlobalMeasureCheck(this.widgetValue.globalMeasureCheckDocId);
        } else {
          this.rows = WraV0Utils.toWraV0Rows(
            this.widgetProperties,
            this.widgetValue,
            this.measureCategories,
            this.globalMeasureCheckValue);
        }
        // this.valueController(widget, this.rows);
        this.checkValidity();
        this.dispatchCompleteState(this.rows);
      }, () => {
        this.dispatchErrorState();
      }
    );
  }

  // private completeLoading() {
  //   this.rows = WraV0Utils.toWraV0Rows(this.widgetValue, this.treatmentSelectedMeasure, this.globalMeasureCheckDocs, this.globalMeasureCheckValue)
  //   this.valueController(widget, preview);
  //   this.checkValidity();
  //   this.dispatchCompleteState(preview);
  // }

  saveWidget(ignoreTrigger?: boolean, resetWarning?: TreatmentWarning[]): void {
    const widget = JSON.parse(JSON.stringify(this.widget)) as UiWidget;
    widget.value = this.widgetValue;

    this.globalMeasureCheckValue = [];

    this.checkValidity();

    this.completeSaveWidget(widget, ignoreTrigger, resetWarning);
  }

  changeRow(row: WraV0Row) {
    const answers: WRAValueAnswer[] = [];
    this.rows.forEach(r => {
      if (r.widgetValue.uuid === row.widgetValue.uuid) {
        answers.push({
          uuid: row.widgetValue.uuid,
          note: row.widgetValue.note,
          implementedValue: row.widgetValue.implementedValue
        });
      } else {
        answers.push({
          uuid: r.widgetValue.uuid,
          note: r.widgetValue.note,
          implementedValue: r.widgetValue.implementedValue
        });
      }
    });
    this.changeFormValue(answers);
  }

  changeFormValue($event: WRAValueAnswer[]) {
    this.widgetValue = {
      globalMeasureCheckDocId: this.widgetValue.globalMeasureCheckDocId,
      answers: $event
    };
    this.checkValidity();

    if (this.internalStatus.isAutoSave && !this.internalStatus.isLoading && this.internalStatus.isDraft) {
      this.saveWidget();
    }
  }

  configuration(): WidgetConfiguration {
    return { supportAutoSave: true, supportValidation: false };
  }

  private checkValidity() {
    const isRequired = this.widget.validators?.find((v: WidgetValidator) => v.key === REQUIRED_VALIDATOR_KEY);
    if (this.widget?.opt) {
      this.isValid$.next(true);
    } else {
      if (isRequired) {
        const isValid = this.rows.filter(r => r.widgetValue.implementedValue === undefined).length === 0;
        this.isValid$.next(isValid);
      } else {
        this.isValid$.next(true);
      }
    }
  }

  clickResetAll() {
    const title = this.translateService.stream('TITLE.WIDGET_RESET_ALL');
    const message = this.translateService.stream('MESSAGE.WIDGET_RESET_ALL');
    this.dialogService.runConfirmDialog({
      title, message, dialogType: DialogType.DELETE, size: 'md'
    }).subscribe(
      (confirmed) => {
        if (confirmed) {
          this.confirmedResetAll();
        }
      }
    );
  }

  private confirmedResetAll() {
    const answers: WRAValueAnswer[] = [];
    this.rows.forEach(r => {
      answers.push({
        uuid: r.widgetValue.uuid,
        note: undefined, // r.widgetValue.note,
        implementedValue: undefined // r.widgetValue.implementedValue,
      });
    });
    this.changeFormValue(answers);
  }
}


